import { useState } from "react";
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import PageForm from '../../templates/PageForm';
import TextEdit from '../../molecules/TextEdit';
import SelectTextField from '../../molecules/SelectTextField';
import { v4 as uuidv4 } from 'uuid';
import ActionButton from "../../molecules/ActionButton";
import { validateField as serviceValidateField } from '../../../services/validationService';
import { isValidEmail } from '../../../utils/EmailUtils';

const BASE_URL = process.env.REACT_APP_BILLER_API_URL + '/bills';
const API_KEY = process.env.REACT_APP_API_KEY || '';

const brazilianStates = [
    'AC', 'AL', 'AP', 'AM', 'BA', 'CE', 'DF', 'ES', 'GO', 'MA', 'MT', 'MS', 
    'MG', 'PA', 'PB', 'PR', 'PE', 'PI', 'RJ', 'RN', 'RS', 'RO', 'RR', 'SC', 
    'SP', 'SE', 'TO'
];

interface FormData {
    // Campos obrigatórios
    id: string;
    data_emissao: string;
    data_vencimento: string;
    valor_titulo: string;
    titulo: string;

    // Pagador (obrigatório)
    pagador_nome_pessoa: string;
    pagador_documento: string;
    pagador_nome_logradouro: string;
    pagador_nome_bairro: string;
    pagador_nome_cidade: string;
    pagador_sigla_UF: string;
    pagador_numero_CEP: string;

    // Pagador (opcionais)
    pagador_nome_fantasia: string;
    pagador_numero: string;
    pagador_complemento: string;
    pagador_codigo_ibge: string;
    pagador_pais: string;
    pagador_telefone: string;
    pagador_email: string;

    // Campos opcionais
    valor_abatimento: string;
    external_id: string;
    mensagem_cobranca: string;
    description: string;
    operacao: string;
    cedente_nome: string;
    nfe: string;
    cfop: string;
    numero_duplicata: string;
    valor_corrigido: string;
    data_vencimento_corrigido: string;
    negativacao_quantidade_dias: string;
    protesto_quantidade_dias: string;

    // Juros
    juros_tipo: string;
    juros_valor: string;
    juros_data: string;

    // Multa
    multa_tipo: string;
    multa_valor: string;
    multa_data: string;

    // Desconto
    desconto_tipo: string;
    desconto_valor: string;
    desconto_data: string;
}

const BillerCreateBill = () => {
    const navigate = useNavigate();
    const [isLoading, setIsLoading] = useState(false);
    const [errors, setErrors] = useState<Record<string, string>>({});
    const [formData, setFormData] = useState<FormData>({
        id: uuidv4(),
        data_emissao: new Date().toISOString().split('T')[0],
        data_vencimento: '',
        valor_titulo: '',
        titulo: '',

        pagador_nome_pessoa: '',
        pagador_documento: '',
        pagador_nome_logradouro: '',
        pagador_nome_bairro: '',
        pagador_nome_cidade: '',
        pagador_sigla_UF: '',
        pagador_numero_CEP: '',

        pagador_nome_fantasia: '',
        pagador_numero: '',
        pagador_complemento: '',
        pagador_codigo_ibge: '',
        pagador_pais: 'Brasil',
        pagador_telefone: '',
        pagador_email: '',

        valor_abatimento: '',
        external_id: '',
        mensagem_cobranca: '',
        description: '',
        operacao: '',
        cedente_nome: '',
        nfe: '',
        cfop: '',
        numero_duplicata: '',
        valor_corrigido: '',
        data_vencimento_corrigido: '',
        negativacao_quantidade_dias: '',
        protesto_quantidade_dias: '',

        juros_tipo: 'ISENTO',
        juros_valor: '',
        juros_data: '',

        multa_tipo: 'ISENTO',
        multa_valor: '',
        multa_data: '',

        desconto_tipo: 'SEM_DESCONTO',
        desconto_valor: '',
        desconto_data: ''
    });

    const handleInputChange = (field: keyof FormData, value: string) => {
        setFormData(prev => ({ ...prev, [field]: value }));
        // Limpa erro do campo quando usuário digita
        if (errors[field]) {
            setErrors(prev => ({ ...prev, [field]: '' }));
        }
    };

    const validateField = (field: keyof FormData, value: string): string => {
        // Campos obrigatórios
        const requiredFields = [
            'data_emissao', 'data_vencimento', 'valor_titulo', 'titulo',
            'pagador_nome_pessoa', 'pagador_documento', 'pagador_nome_logradouro',
            'pagador_nome_bairro', 'pagador_nome_cidade', 'pagador_sigla_UF', 'pagador_numero_CEP'
        ];

        if (requiredFields.includes(field) && !value) {
            return 'Campo obrigatório';
        }

        if (!value) return ''; // Campos opcionais vazios são válidos

        // Usar validações do serviço existente
        switch (field) {
            case 'data_emissao':
            case 'data_vencimento':
                const dateValidation = serviceValidateField(value.replace(/-/g, '/').split('/').reverse().join('/'), 'date', 'Data');
                return dateValidation.isValid ? '' : 'Formato de data inválido';

            case 'valor_titulo':
                if (!/^[0-9]{1,15}(\.[0-9]{1,2})?$/.test(value)) return 'Formato de valor inválido';
                if (parseFloat(value) <= 0) return 'Valor deve ser maior que zero';
                return '';

            case 'titulo':
                const titleValidation = serviceValidateField(value, 'number', 'Título', 8);
                return titleValidation.isValid ? '' : 'Deve conter apenas números (máx 8 dígitos)';

            case 'pagador_documento':
                const docValidation = serviceValidateField(value, 'taxDocument', 'Documento');
                return docValidation.isValid ? '' : 'CPF (11 dígitos) ou CNPJ (14 dígitos)';

            case 'pagador_email':
                return isValidEmail(value) ? '' : 'Email inválido';

            case 'pagador_telefone':
                const phoneValidation = serviceValidateField(value, 'phone', 'Telefone');
                return phoneValidation.isValid ? '' : 'Telefone inválido';

            case 'pagador_sigla_UF':
                return brazilianStates.includes(value) ? '' : 'Estado inválido';

            case 'pagador_numero_CEP':
                return /^\d{8}$/.test(value) ? '' : 'CEP deve conter 8 dígitos';

            case 'pagador_codigo_ibge':
                return /^\d{7}$/.test(value) ? '' : 'Código IBGE deve conter 7 dígitos';

            case 'cfop':
                return /^\d{4}$/.test(value) ? '' : 'CFOP deve conter 4 dígitos';

            case 'negativacao_quantidade_dias':
                const negNum = parseInt(value);
                return (!isNaN(negNum) && negNum >= 2 && negNum <= 99) ? '' : 'Deve estar entre 2 e 99 dias';

            case 'protesto_quantidade_dias':
                const protNum = parseInt(value);
                return (!isNaN(protNum) && protNum >= 1 && protNum <= 99) ? '' : 'Deve estar entre 1 e 99 dias';

            case 'operacao':
                const opValidation = serviceValidateField(value, 'text', 'Operação', 10);
                return opValidation.isValid ? '' : 'Máximo 10 caracteres';

            case 'cedente_nome':
                const cedenteValidation = serviceValidateField(value, 'text', 'Cedente', 100);
                return cedenteValidation.isValid ? '' : 'Máximo 100 caracteres';

            case 'nfe':
                const nfeValidation = serviceValidateField(value, 'text', 'NFe', 100);
                return nfeValidation.isValid ? '' : 'Máximo 100 caracteres';

            case 'numero_duplicata':
                const dupValidation = serviceValidateField(value, 'text', 'Duplicata', 10);
                return dupValidation.isValid ? '' : 'Máximo 10 caracteres';

            case 'external_id':
                const extValidation = serviceValidateField(value, 'text', 'External ID', 36);
                return extValidation.isValid ? '' : 'Máximo 36 caracteres';

            default:
                return '';
        }
    };

    const validateForm = (): boolean => {
        const newErrors: Record<string, string> = {};

        // Campos obrigatórios
        const requiredFields: (keyof FormData)[] = [
            'data_emissao', 'data_vencimento', 'valor_titulo', 'titulo',
            'pagador_nome_pessoa', 'pagador_documento', 'pagador_nome_logradouro',
            'pagador_nome_bairro', 'pagador_nome_cidade', 'pagador_sigla_UF', 'pagador_numero_CEP'
        ];

        requiredFields.forEach(field => {
            const error = validateField(field, formData[field]);
            if (error) newErrors[field] = error;
        });

        // Validar campos opcionais se preenchidos
        Object.keys(formData).forEach(key => {
            const field = key as keyof FormData;
            if (!requiredFields.includes(field) && formData[field]) {
                const error = validateField(field, formData[field]);
                if (error) newErrors[field] = error;
            }
        });

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const buildPayload = () => {
        const payload: any = {
            id: formData.id,
            data_emissao: formData.data_emissao,
            data_vencimento: formData.data_vencimento,
            valor_titulo: parseFloat(formData.valor_titulo),
            titulo: formData.titulo,
            pagador: {
                nome_pessoa: formData.pagador_nome_pessoa,
                documento: formData.pagador_documento,
                nome_logradouro: formData.pagador_nome_logradouro,
                nome_bairro: formData.pagador_nome_bairro,
                nome_cidade: formData.pagador_nome_cidade,
                sigla_UF: formData.pagador_sigla_UF,
                numero_CEP: formData.pagador_numero_CEP
            }
        };

        // Campos opcionais do pagador
        if (formData.pagador_nome_fantasia) payload.pagador.nome_fantasia = formData.pagador_nome_fantasia;
        if (formData.pagador_numero) payload.pagador.numero = formData.pagador_numero;
        if (formData.pagador_complemento) payload.pagador.complemento = formData.pagador_complemento;
        if (formData.pagador_codigo_ibge) payload.pagador.codigo_ibge = formData.pagador_codigo_ibge;
        if (formData.pagador_pais) payload.pagador.pais = formData.pagador_pais;
        if (formData.pagador_telefone) payload.pagador.telefone = formData.pagador_telefone;
        if (formData.pagador_email) payload.pagador.email = formData.pagador_email;

        // Campos opcionais gerais
        if (formData.valor_abatimento) payload.valor_abatimento = parseFloat(formData.valor_abatimento);
        if (formData.external_id) payload.external_id = formData.external_id;
        if (formData.mensagem_cobranca) payload.mensagem_cobranca = formData.mensagem_cobranca;
        if (formData.description) payload.description = formData.description;
        if (formData.operacao) payload.operacao = formData.operacao;
        if (formData.cedente_nome) payload.cedente_nome = formData.cedente_nome;
        if (formData.nfe) payload.nfe = formData.nfe;
        if (formData.cfop) payload.cfop = formData.cfop;
        if (formData.numero_duplicata) payload.numero_duplicata = formData.numero_duplicata;
        if (formData.valor_corrigido) payload.valor_corrigido = parseFloat(formData.valor_corrigido);
        if (formData.data_vencimento_corrigido) payload.data_vencimento_corrigido = formData.data_vencimento_corrigido;
        if (formData.negativacao_quantidade_dias) payload.negativacao_quantidade_dias = parseInt(formData.negativacao_quantidade_dias);
        if (formData.protesto_quantidade_dias) payload.protesto_quantidade_dias = parseInt(formData.protesto_quantidade_dias);

        // Juros
        if (formData.juros_tipo !== 'ISENTO') {
            payload.juros = {
                tipo_juros: formData.juros_tipo,
                valor: formData.juros_valor ? parseFloat(formData.juros_valor) : null,
                data_juros: formData.juros_data || null
            };
        }

        // Multa
        if (formData.multa_tipo !== 'ISENTO') {
            payload.multa = {
                tipo_multa: formData.multa_tipo,
                valor: formData.multa_valor ? parseFloat(formData.multa_valor) : null,
                data_multa: formData.multa_data || null
            };
        }

        // Desconto
        if (formData.desconto_tipo !== 'SEM_DESCONTO') {
            payload.desconto = {
                tipo_desconto: formData.desconto_tipo,
                valor: formData.desconto_valor ? parseFloat(formData.desconto_valor) : null,
                data_desconto: formData.desconto_data || null
            };
        }

        return payload;
    };

    const resetForm = () => {
        setFormData({
            id: uuidv4(),
            data_emissao: new Date().toISOString().split('T')[0],
            data_vencimento: '',
            valor_titulo: '',
            titulo: '',

            pagador_nome_pessoa: '',
            pagador_documento: '',
            pagador_nome_logradouro: '',
            pagador_nome_bairro: '',
            pagador_nome_cidade: '',
            pagador_sigla_UF: '',
            pagador_numero_CEP: '',

            pagador_nome_fantasia: '',
            pagador_numero: '',
            pagador_complemento: '',
            pagador_codigo_ibge: '',
            pagador_pais: 'Brasil',
            pagador_telefone: '',
            pagador_email: '',

            valor_abatimento: '',
            external_id: '',
            mensagem_cobranca: '',
            description: '',
            operacao: '',
            cedente_nome: '',
            nfe: '',
            cfop: '',
            numero_duplicata: '',
            valor_corrigido: '',
            data_vencimento_corrigido: '',
            negativacao_quantidade_dias: '',
            protesto_quantidade_dias: '',

            juros_tipo: 'ISENTO',
            juros_valor: '',
            juros_data: '',

            multa_tipo: 'ISENTO',
            multa_valor: '',
            multa_data: '',

            desconto_tipo: 'SEM_DESCONTO',
            desconto_valor: '',
            desconto_data: ''
        });
        setErrors({});
    };

    const handleSubmit = async () => {
        if (!validateForm()) {
            toast.error('Por favor, corrija os erros no formulário');
            return;
        }

        try {
            setIsLoading(true);
            const payload = buildPayload();

            const response = await fetch(BASE_URL, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'x-api-key': API_KEY
                },
                body: JSON.stringify(payload)
            });

            if (response.ok) {
                toast.success('Boleto criado com sucesso!');
                resetForm();
            } else {
                const error = await response.json();
                toast.error(error.message || 'Erro ao criar boleto');
            }
        } catch (error) {
            toast.error('Erro de conexão');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <PageForm
            pageTitle="Criar Boleto"
            isLoading={isLoading}
            breadcrumb={[
                { label: 'Financeiro', url: '/financial' },
                { label: 'Lista de Boletos', url: '/financial/biller' },
                { label: 'Criar Boleto', url: '#' }
            ]}
            apiVersion=""
            controlSlot={
                <div className="pt-4 flex justify-end gap-2">
                    <ActionButton
                        label="Criação em Lote"
                        className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600 disabled:opacity-50"
                        onClick={() => navigate('/financial/biller/batch-create')}
                    />
                    <button
                        onClick={handleSubmit}
                        disabled={isLoading}
                        className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
                    >
                        Criar Boleto
                    </button>
                </div>
            }
        >
            <div className="space-y-6">
                {/* Dados Básicos */}
                <div className="bg-white p-6 rounded-lg shadow">
                    <h3 className="text-lg font-medium mb-4">Dados Básicos</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                        <TextEdit
                            label="Data de Emissão *"
                            type="date"
                            value={formData.data_emissao}
                            onChange={(e) => handleInputChange('data_emissao', e.target.value)}
                            errorMessage={errors.data_emissao}
                            required
                        />
                        <TextEdit
                            label="Data de Vencimento *"
                            type="date"
                            value={formData.data_vencimento}
                            onChange={(e) => handleInputChange('data_vencimento', e.target.value)}
                            errorMessage={errors.data_vencimento}
                            required
                        />
                        <TextEdit
                            label="Valor do Título *"
                            type="number"
                            step="0.01"
                            value={formData.valor_titulo}
                            onChange={(e) => handleInputChange('valor_titulo', e.target.value)}
                            errorMessage={errors.valor_titulo}
                            required
                        />
                        <TextEdit
                            label="Número do Título *"
                            type="number"
                            step="1"
                            min={0}
                            max={999999}
                            value={formData.titulo}
                            onChange={(e) => handleInputChange('titulo', e.target.value)}
                            errorMessage={errors.titulo}
                            required
                        />
                    </div>
                </div>

                {/* Dados do Pagador */}
                <div className="bg-white p-6 rounded-lg shadow">
                    <h3 className="text-lg font-medium mb-4">Dados do Pagador</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                        <TextEdit
                            label="Nome/Razão Social *"
                            value={formData.pagador_nome_pessoa}
                            onChange={(e) => handleInputChange('pagador_nome_pessoa', e.target.value)}
                            errorMessage={errors.pagador_nome_pessoa}
                            required
                        />
                        <TextEdit
                            label="Nome Fantasia"
                            value={formData.pagador_nome_fantasia}
                            onChange={(e) => handleInputChange('pagador_nome_fantasia', e.target.value)}
                        />
                        <TextEdit
                            label="Documento (CPF/CNPJ) *"
                            value={formData.pagador_documento}
                            onChange={(e) => handleInputChange('pagador_documento', e.target.value)}
                            errorMessage={errors.pagador_documento}
                            required
                        />
                        <TextEdit
                            label="Email"
                            type="email"
                            value={formData.pagador_email}
                            onChange={(e) => handleInputChange('pagador_email', e.target.value)}
                            errorMessage={errors.pagador_email}
                        />
                        <TextEdit
                            label="Telefone"
                            value={formData.pagador_telefone}
                            onChange={(e) => handleInputChange('pagador_telefone', e.target.value)}
                            errorMessage={errors.pagador_telefone}
                        />
                        <TextEdit
                            label="País"
                            value={formData.pagador_pais}
                            onChange={(e) => handleInputChange('pagador_pais', e.target.value)}
                        />
                    </div>
                </div>

                {/* Endereço do Pagador */}
                <div className="bg-white p-6 rounded-lg shadow">
                    <h3 className="text-lg font-medium mb-4">Endereço do Pagador</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                        <TextEdit
                            label="Logradouro *"
                            value={formData.pagador_nome_logradouro}
                            onChange={(e) => handleInputChange('pagador_nome_logradouro', e.target.value)}
                            errorMessage={errors.pagador_nome_logradouro}
                            required
                        />
                        <TextEdit
                            label="Número"
                            value={formData.pagador_numero}
                            onChange={(e) => handleInputChange('pagador_numero', e.target.value)}
                        />
                        <TextEdit
                            label="Complemento"
                            value={formData.pagador_complemento}
                            onChange={(e) => handleInputChange('pagador_complemento', e.target.value)}
                        />
                        <TextEdit
                            label="Bairro *"
                            value={formData.pagador_nome_bairro}
                            onChange={(e) => handleInputChange('pagador_nome_bairro', e.target.value)}
                            errorMessage={errors.pagador_nome_bairro}
                            required
                        />
                        <TextEdit
                            label="Cidade *"
                            value={formData.pagador_nome_cidade}
                            onChange={(e) => handleInputChange('pagador_nome_cidade', e.target.value)}
                            errorMessage={errors.pagador_nome_cidade}
                            required
                        />
                        <SelectTextField
                            label="UF *"
                            value={formData.pagador_sigla_UF}
                            onChange={(value) => handleInputChange('pagador_sigla_UF', value)}
                            items={brazilianStates.map(uf => ({ value: uf, label: uf }))}
                            errorMessage={errors.pagador_sigla_UF}
                            required
                        />
                        <TextEdit
                            label="CEP *"
                            value={formData.pagador_numero_CEP}
                            onChange={(e) => handleInputChange('pagador_numero_CEP', e.target.value)}
                            errorMessage={errors.pagador_numero_CEP}
                            required
                        />
                        <TextEdit
                            label="Código IBGE"
                            value={formData.pagador_codigo_ibge}
                            onChange={(e) => handleInputChange('pagador_codigo_ibge', e.target.value)}
                            errorMessage={errors.pagador_codigo_ibge}
                        />
                    </div>
                </div>

                {/* Juros, Multa, Desconto, Abatimento */}
                <div className="bg-white p-6 rounded-lg shadow">
                    <h3 className="text-lg font-medium mb-4">Juros, Multa, Desconto, Abatimento</h3>
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                        {/* Juros */}
                        <div>
                            <h4 className="font-medium mb-2">Juros</h4>
                            <div className="space-y-2">
                                <SelectTextField
                                    label="Tipo de Juros"
                                    value={formData.juros_tipo}
                                    onChange={(e) => handleInputChange('juros_tipo', e.target.value)}
                                    items={[
                                        { value: 'ISENTO', label: 'Isento' },
                                        { value: 'PERCENTUAL_MENSAL', label: 'Percentual Mensal' },
                                        { value: 'PERCENTUAL_DIARIO', label: 'Percentual Diário' },
                                        { value: 'PERCENTUAL_ANUAL', label: 'Percentual Anual' },
                                        { value: 'VALOR_DIARIO', label: 'Valor Diário' }
                                    ]}
                                />
                                {formData.juros_tipo !== 'ISENTO' && (
                                    <>
                                        <TextEdit
                                            label="Valor"
                                            type="number"
                                            step="0.01"
                                            value={formData.juros_valor}
                                            onChange={(e) => handleInputChange('juros_valor', e.target.value)}
                                        />
                                        <TextEdit
                                            label="Data de Juros"
                                            type="date"
                                            value={formData.juros_data}
                                            onChange={(e) => handleInputChange('juros_data', e.target.value)}
                                        />
                                    </>
                                )}
                            </div>
                        </div>

                        {/* Multa */}
                        <div>
                            <h4 className="font-medium mb-2">Multa</h4>
                            <div className="space-y-2">
                                <SelectTextField
                                    label="Tipo de Multa"
                                    value={formData.multa_tipo}
                                    onChange={(e) => handleInputChange('multa_tipo', e.target.value)}
                                    items={[
                                        { value: 'ISENTO', label: 'Isento' },
                                        { value: 'VALOR_FIXO', label: 'Valor Fixo' },
                                        { value: 'VALOR_PERCENTUAL', label: 'Valor Percentual' }
                                    ]}
                                />
                                {formData.multa_tipo !== 'ISENTO' && (
                                    <>
                                        <TextEdit
                                            label="Valor"
                                            type="number"
                                            step="0.01"
                                            value={formData.multa_valor}
                                            onChange={(e) => handleInputChange('multa_valor', e.target.value)}
                                        />
                                        <TextEdit
                                            label="Data de Multa"
                                            type="date"
                                            value={formData.multa_data}
                                            onChange={(e) => handleInputChange('multa_data', e.target.value)}
                                        />
                                    </>
                                )}
                            </div>
                        </div>

                        {/* Desconto */}
                        <div>
                            <h4 className="font-medium mb-2">Desconto</h4>
                            <div className="space-y-2">
                                <SelectTextField
                                    label="Tipo de Desconto"
                                    value={formData.desconto_tipo}
                                    onChange={(e) => handleInputChange('desconto_tipo', e.target.value)}
                                    items={[
                                        { value: 'SEM_DESCONTO', label: 'Sem Desconto' },
                                        { value: 'VALOR_FIXO', label: 'Valor Fixo' },
                                        { value: 'PERCENTUAL_MENSAL', label: 'Percentual Mensal' },
                                        { value: 'PERCENTUAL_DINAMICO', label: 'Percentual Dinâmico' },
                                        { value: 'VALOR_FIXO_DINAMICO', label: 'Valor Fixo Dinâmico' }
                                    ]}
                                />
                                {formData.desconto_tipo !== 'SEM_DESCONTO' && (
                                    <>
                                        <TextEdit
                                            label="Valor"
                                            type="number"
                                            step="0.01"
                                            value={formData.desconto_valor}
                                            onChange={(e) => handleInputChange('desconto_valor', e.target.value)}
                                        />
                                        <TextEdit
                                            label="Data de Desconto"
                                            type="date"
                                            value={formData.desconto_data}
                                            onChange={(e) => handleInputChange('desconto_data', e.target.value)}
                                        />
                                    </>
                                )}
                            </div>
                        </div>
                        <div>
                            <h4 className="font-medium mb-2">Abatimento</h4>
                            <div className="space-y-2">
                                <TextEdit
                                    label="Valor do Abatimento"
                                    type="number"
                                    step="0.01"
                                    value={formData.valor_abatimento}
                                    onChange={(e) => handleInputChange('valor_abatimento', e.target.value)}
                                />
                            </div>
                        </div>
                    </div>
                </div>

                {/* Campos Adicionais */}
                <div className="bg-white p-6 rounded-lg shadow">
                    <h3 className="text-lg font-medium mb-4">Informações Adicionais</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                        <TextEdit
                            label="Mensagem de Cobrança (aparece na parte superior do boleto)"
                            value={formData.mensagem_cobranca}
                            onChange={(e) => handleInputChange('mensagem_cobranca', e.target.value)}
                        />
                        <TextEdit
                            label="Descrição (aparece na parte inferior do boleto)"
                            value={formData.description}
                            onChange={(e) => handleInputChange('description', e.target.value)}
                        />
                        <TextEdit
                            label="Operação"
                            value={formData.operacao}
                            onChange={(e) => handleInputChange('operacao', e.target.value)}
                            errorMessage={errors.operacao}
                        />
                        <TextEdit
                            label="Cedente Nome"
                            value={formData.cedente_nome}
                            onChange={(e) => handleInputChange('cedente_nome', e.target.value)}
                            errorMessage={errors.cedente_nome}
                        />
                        <TextEdit
                            label="NFe"
                            value={formData.nfe}
                            onChange={(e) => handleInputChange('nfe', e.target.value)}
                            errorMessage={errors.nfe}
                        />
                        <TextEdit
                            label="CFOP"
                            value={formData.cfop}
                            onChange={(e) => handleInputChange('cfop', e.target.value)}
                            errorMessage={errors.cfop}
                        />
                        <TextEdit
                            label="Número Duplicata"
                            value={formData.numero_duplicata}
                            onChange={(e) => handleInputChange('numero_duplicata', e.target.value)}
                            errorMessage={errors.numero_duplicata}
                        />
                        <TextEdit
                            label="Negativação (dias)"
                            type="number"
                            min="2"
                            max="99"
                            value={formData.negativacao_quantidade_dias}
                            onChange={(e) => handleInputChange('negativacao_quantidade_dias', e.target.value)}
                            errorMessage={errors.negativacao_quantidade_dias}
                        />
                        <TextEdit
                            label="Protesto (dias)"
                            type="number"
                            min="1"
                            max="99"
                            value={formData.protesto_quantidade_dias}
                            onChange={(e) => handleInputChange('protesto_quantidade_dias', e.target.value)}
                            errorMessage={errors.protesto_quantidade_dias}
                        />
                        <TextEdit
                            label="External ID"
                            value={formData.external_id}
                            onChange={(e) => handleInputChange('external_id', e.target.value)}
                            errorMessage={errors.external_id}
                        />
                    </div>
                </div>
            </div>
        </PageForm>
    );
};

export default BillerCreateBill;